import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react'
import { auth } from '../services/firebase'
import { signInWithCustomToken, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth'
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore'
import { db } from '../services/firebase'
import { authApi } from '../services/authApi'
import toast from 'react-hot-toast'

// Initial state
const initialState = {
    user: null,
    userProfile: null,
    isAuthenticated: false,
    authInitialized: false,
    phoneNumber: null,
    userRole: null,
    userData: null,
    companyData: null,
    dealerships: null,
    dealershipsCount: 0,
    selectedOrganization: null,
    organizationType: null,
    currentDealership: null, // Currently active dealership for dealer/member
    unsubscribeUserProfile: null
}

// Action types
const AUTH_ACTIONS = {
    SET_AUTH_INITIALIZED: 'SET_AUTH_INITIALIZED',
    SET_USER: 'SET_USER',
    SET_USER_PROFILE: 'SET_USER_PROFILE',
    SET_AUTHENTICATED: 'SET_AUTHENTICATED',
    SET_PHONE_NUMBER: 'SET_PHONE_NUMBER',
    SET_USER_ROLE: 'SET_USER_ROLE',
    SET_USER_DATA: 'SET_USER_DATA',
    SET_COMPANY_DATA: 'SET_COMPANY_DATA',
    SET_DEALERSHIPS: 'SET_DEALERSHIPS',
    SET_DEALERSHIPS_COUNT: 'SET_DEALERSHIPS_COUNT',
    SET_SELECTED_ORGANIZATION: 'SET_SELECTED_ORGANIZATION',
    SET_ORGANIZATION_TYPE: 'SET_ORGANIZATION_TYPE',
    SET_CURRENT_DEALERSHIP: 'SET_CURRENT_DEALERSHIP',
    SET_UNSUBSCRIBE_USER_PROFILE: 'SET_UNSUBSCRIBE_USER_PROFILE',
    CLEAR_AUTH: 'CLEAR_AUTH',
    CLEAR_TEMP_STATE: 'CLEAR_TEMP_STATE'
}

// Reducer function
const authReducer = (state, action) => {
    switch (action.type) {
        case AUTH_ACTIONS.SET_AUTH_INITIALIZED:
            return { ...state, authInitialized: action.payload }

        case AUTH_ACTIONS.SET_USER:
            return { ...state, user: action.payload }

        case AUTH_ACTIONS.SET_USER_PROFILE:
            return { ...state, userProfile: action.payload }

        case AUTH_ACTIONS.SET_AUTHENTICATED:
            return { ...state, isAuthenticated: action.payload }

        case AUTH_ACTIONS.SET_PHONE_NUMBER:
            return { ...state, phoneNumber: action.payload }

        case AUTH_ACTIONS.SET_USER_ROLE:
            return { ...state, userRole: action.payload }

        case AUTH_ACTIONS.SET_USER_DATA:
            return { ...state, userData: action.payload }

        case AUTH_ACTIONS.SET_COMPANY_DATA:
            return { ...state, companyData: action.payload }

        case AUTH_ACTIONS.SET_DEALERSHIPS:
            return { ...state, dealerships: action.payload }

        case AUTH_ACTIONS.SET_DEALERSHIPS_COUNT:
            return { ...state, dealershipsCount: action.payload }

        case AUTH_ACTIONS.SET_SELECTED_ORGANIZATION:
            return { ...state, selectedOrganization: action.payload }

        case AUTH_ACTIONS.SET_ORGANIZATION_TYPE:
            return { ...state, organizationType: action.payload }

        case AUTH_ACTIONS.SET_CURRENT_DEALERSHIP:
            return { ...state, currentDealership: action.payload }

        case AUTH_ACTIONS.SET_UNSUBSCRIBE_USER_PROFILE:
            return { ...state, unsubscribeUserProfile: action.payload }

        case AUTH_ACTIONS.CLEAR_AUTH:
            return {
                ...initialState,
                authInitialized: true
            }

        case AUTH_ACTIONS.CLEAR_TEMP_STATE:
            return {
                ...state,
                phoneNumber: null,
                selectedOrganization: null,
                organizationType: null
            }

        default:
            return state
    }
}

// Create context
const AuthContext = createContext(null)

// Auth provider component
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState)

    // Initialize Firebase authentication listener
    const initializeAuth = useCallback(async () => {
        try {
            if (!auth) {
                console.warn('Firebase auth not initialized')
                return
            }

            // Set up Firebase auth state listener
            onAuthStateChanged(auth, async (firebaseUser) => {

                if (firebaseUser) {
                    // User is signed in - set up real-time listener for user profile
                    try {
                        const userDocRef = doc(db, 'users', firebaseUser.uid)

                        // Set up real-time listener for user profile
                        const unsubscribe = onSnapshot(userDocRef, (userDoc) => {
                            if (userDoc.exists()) {
                                const userData = userDoc.data()

                                dispatch({
                                    type: AUTH_ACTIONS.SET_USER, payload: {
                                        uid: firebaseUser.uid,
                                        phoneNumber: firebaseUser.phoneNumber,
                                        email: firebaseUser.email
                                    }
                                })
                                dispatch({ type: AUTH_ACTIONS.SET_USER_PROFILE, payload: userData })
                                dispatch({ type: AUTH_ACTIONS.SET_AUTHENTICATED, payload: true })
                                dispatch({ type: AUTH_ACTIONS.SET_AUTH_INITIALIZED, payload: true })
                                dispatch({ type: AUTH_ACTIONS.SET_UNSUBSCRIBE_USER_PROFILE, payload: unsubscribe })
                                
                                // Load dealerships from user profile if available (for dealers and members)
                                if (userData.dealerships && Array.isArray(userData.dealerships)) {
                                    dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS, payload: userData.dealerships })
                                    dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS_COUNT, payload: userData.dealerships.length })
                                }
                            } else {
                                console.error('User document not found in Firestore')
                                firebaseSignOut(auth)
                            }
                        }, (error) => {
                            console.error('Error in user profile listener:', error)
                            firebaseSignOut(auth)
                        })

                    } catch (error) {
                        console.error('Error setting up user profile listener:', error)
                        firebaseSignOut(auth)
                    }
                } else {
                    // User is signed out
                    dispatch({ type: AUTH_ACTIONS.CLEAR_AUTH })
                }
            })
        } catch (error) {
            console.error('Error initializing auth:', error)
            dispatch({ type: AUTH_ACTIONS.SET_AUTH_INITIALIZED, payload: false })
        }
    }, [])

    // Send admin login request - validates user, sends OTP, and returns user details
    const adminLogin = useCallback(async (phoneNumber) => {
        try {
            const response = await authApi.adminLogin(phoneNumber)

            if (response.success) {
                // Prepare all state updates
                dispatch({ type: AUTH_ACTIONS.SET_PHONE_NUMBER, payload: phoneNumber })
                dispatch({ type: AUTH_ACTIONS.SET_USER_ROLE, payload: response.role })

                // Add role-specific data
                if (response.role === 'admin') {
                    dispatch({ type: AUTH_ACTIONS.SET_USER_DATA, payload: response.userData })
                } else if (response.role === 'dealer') {
                    dispatch({ type: AUTH_ACTIONS.SET_COMPANY_DATA, payload: response.companyData })
                    dispatch({ type: AUTH_ACTIONS.SET_USER_DATA, payload: { companyId: response.companyId } })
                    // For dealers, also set dealerships if they have multiple
                    if (response.dealerships) {
                        dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS, payload: response.dealerships })
                        dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS_COUNT, payload: response.dealershipsCount })
                        // Store in localStorage for persistence
                        localStorage.setItem('userDealerships', JSON.stringify(response.dealerships))
                        
                        // Auto-select first dealership if only one
                        if (response.dealerships.length === 1) {
                            dispatch({ type: AUTH_ACTIONS.SET_CURRENT_DEALERSHIP, payload: response.dealerships[0] })
                            localStorage.setItem('currentDealership', JSON.stringify(response.dealerships[0]))
                        }
                    }
                } else if (response.role === 'member') {
                    dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS, payload: response.dealerships })
                    dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS_COUNT, payload: response.dealershipsCount })
                    // Store in localStorage for persistence
                    if (response.dealerships) {
                        localStorage.setItem('userDealerships', JSON.stringify(response.dealerships))
                        
                        // Auto-select first dealership if only one
                        if (response.dealerships.length === 1) {
                            dispatch({ type: AUTH_ACTIONS.SET_CURRENT_DEALERSHIP, payload: response.dealerships[0] })
                            localStorage.setItem('currentDealership', JSON.stringify(response.dealerships[0]))
                        }
                    }
                }

                // Determine return value based on role and requirements
                if ((response.role === 'member' || response.role === 'dealer') && response.dealerships && response.dealerships.length > 1) {
                    return {
                        success: true,
                        role: response.role,
                        requiresSelection: true,
                        dealerships: response.dealerships
                    }
                } else {
                    return { success: true, role: response.role, requiresSelection: false }
                }
            } else {
                toast.error(response.message || 'Access denied')
                return { success: false, message: response.message }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Access denied. Please check your credentials.'
            toast.error(errorMessage)
            return { success: false, message: errorMessage }
        }
    }, [])

    // Verify OTP and sign in with Firebase
    const verifyOTP = useCallback(async (otp) => {
        try {
            if (!state.phoneNumber) {
                toast.error('No phone number found. Please request OTP again.')
                return false
            }

            const response = await authApi.verifyOtp(state.phoneNumber, otp)

            if (response.success && response.verified && response.customToken) {
                // Sign in with Firebase custom token
                const userCredential = await signInWithCustomToken(auth, response.customToken)
                const firebaseUser = userCredential.user

                // Clear temporary state
                dispatch({ type: AUTH_ACTIONS.CLEAR_TEMP_STATE })

                toast.success('Login successful!')
                return { success: true }
            } else {
                toast.error('Invalid OTP. Please try again.')
                return false
            }
        } catch (error) {
            toast.error('Invalid OTP. Please try again.')
            return false
        }
    }, [state.phoneNumber])

    // Select organization (company or dealership) for login
    const selectOrganization = useCallback((organization, type) => {
        dispatch({ type: AUTH_ACTIONS.SET_SELECTED_ORGANIZATION, payload: organization })
        dispatch({ type: AUTH_ACTIONS.SET_ORGANIZATION_TYPE, payload: type })
        dispatch({ type: AUTH_ACTIONS.SET_CURRENT_DEALERSHIP, payload: organization })
        
        // Store in localStorage for persistence
        localStorage.setItem('currentDealership', JSON.stringify(organization))
        
        return true
    }, [])

    // Load dealerships for authenticated users
    const loadUserDealerships = useCallback(async () => {
        if (!state.user?.uid || !db) return
        
        try {
            const userDocRef = doc(db, 'users', state.user.uid)
            const userDoc = await getDoc(userDocRef)
            
            if (userDoc.exists()) {
                const userData = userDoc.data()
                if (userData.dealerships && Array.isArray(userData.dealerships)) {
                    dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS, payload: userData.dealerships })
                    dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS_COUNT, payload: userData.dealerships.length })
                    
                    // Store in localStorage
                    localStorage.setItem('userDealerships', JSON.stringify(userData.dealerships))
                    
                    // Auto-select first dealership if only one and no current dealership set
                    if (userData.dealerships.length === 1 && !state.currentDealership) {
                        dispatch({ type: AUTH_ACTIONS.SET_CURRENT_DEALERSHIP, payload: userData.dealerships[0] })
                        localStorage.setItem('currentDealership', JSON.stringify(userData.dealerships[0]))
                    }
                }
            }
        } catch (error) {
            console.error('Error loading user dealerships:', error)
        }
    }, [state.user?.uid, state.currentDealership])

    // Switch dealership (for dealers/members with multiple dealerships)
    const switchDealership = useCallback((dealership) => {
        dispatch({ type: AUTH_ACTIONS.SET_CURRENT_DEALERSHIP, payload: dealership })
        
        // Store in localStorage for persistence
        localStorage.setItem('currentDealership', JSON.stringify(dealership))
        
        toast.success(`Switched to ${dealership.name}`)
        
        // Optionally reload the page to refresh data for new dealership
        window.location.reload()
    }, [])

    // Sign out
    const signOut = useCallback(async () => {
        try {
            if (auth) {
                await firebaseSignOut(auth)
            }
        } catch (error) {
            console.error('Error signing out from Firebase:', error)
        }

        // Clear localStorage
        localStorage.removeItem('currentDealership')
        localStorage.removeItem('userDealerships')

        // Clear local state (this will also clean up listeners)
        dispatch({ type: AUTH_ACTIONS.CLEAR_AUTH })
        toast.success('Signed out successfully')
    }, [])

    // Resend OTP
    const resendOTP = useCallback(async () => {
        if (!state.phoneNumber) {
            toast.error('No phone number found')
            return false
        }
        return await adminLogin(state.phoneNumber)
    }, [state.phoneNumber, adminLogin])

    // Force refresh user profile from Firestore
    const refreshUserProfile = useCallback(async () => {
        if (!state.user?.uid) {
            console.warn('No user UID found for profile refresh')
            return false
        }

        try {
            const userDocRef = doc(db, 'users', state.user.uid)
            const userDoc = await getDoc(userDocRef)

            if (userDoc.exists()) {
                const userData = userDoc.data()
                dispatch({ type: AUTH_ACTIONS.SET_USER_PROFILE, payload: userData })
                return true
            } else {
                console.error('User document not found during refresh')
                return false
            }
        } catch (error) {
            console.error('Error refreshing user profile:', error)
            return false
        }
    }, [state.user?.uid])

    // Update user profile in Firestore
    const updateProfile = useCallback(async (profileData) => {
        if (!state.user?.uid) {
            console.warn('No user UID found for profile update')
            return false
        }

        try {
            const userDocRef = doc(db, 'users', state.user.uid)
            await updateDoc(userDocRef, {
                ...profileData,
                updatedAt: new Date().toISOString()
            })

            // Refresh profile after update
            await refreshUserProfile()
            toast.success('Profile updated successfully!')
            return true
        } catch (error) {
            console.error('Error updating user profile:', error)
            toast.error('Failed to update profile')
            return false
        }
    }, [state.user?.uid, refreshUserProfile])

    // Initialize auth on mount - only run once
    useEffect(() => {
        initializeAuth()
    }, []) // Empty dependency array - run only once on mount

    // Load dealerships when user is authenticated
    useEffect(() => {
        if (state.isAuthenticated && state.user?.uid && !state.dealerships) {
            loadUserDealerships()
        }
    }, [state.isAuthenticated, state.user?.uid, state.dealerships, loadUserDealerships])

    // Restore dealerships and current dealership from localStorage on mount
    useEffect(() => {
        if (state.isAuthenticated) {
            // Restore dealerships list
            if (!state.dealerships) {
                const storedDealerships = localStorage.getItem('userDealerships')
                if (storedDealerships) {
                    try {
                        const dealerships = JSON.parse(storedDealerships)
                        if (Array.isArray(dealerships) && dealerships.length > 0) {
                            dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS, payload: dealerships })
                            dispatch({ type: AUTH_ACTIONS.SET_DEALERSHIPS_COUNT, payload: dealerships.length })
                            
                            // Auto-select first dealership if only one and no current dealership set
                            if (dealerships.length === 1 && !state.currentDealership) {
                                dispatch({ type: AUTH_ACTIONS.SET_CURRENT_DEALERSHIP, payload: dealerships[0] })
                                localStorage.setItem('currentDealership', JSON.stringify(dealerships[0]))
                            }
                        }
                    } catch (error) {
                        console.error('Error parsing stored dealerships:', error)
                        localStorage.removeItem('userDealerships')
                    }
                }
            }
            
            // Restore current dealership
            if (!state.currentDealership) {
                const storedDealership = localStorage.getItem('currentDealership')
                if (storedDealership) {
                    try {
                        const dealership = JSON.parse(storedDealership)
                        dispatch({ type: AUTH_ACTIONS.SET_CURRENT_DEALERSHIP, payload: dealership })
                    } catch (error) {
                        console.error('Error parsing stored dealership:', error)
                        localStorage.removeItem('currentDealership')
                    }
                }
            }
        }
    }, [state.isAuthenticated, state.currentDealership, state.dealerships])

    // Cleanup effect for unsubscribe function
    useEffect(() => {
        return () => {
            if (state.unsubscribeUserProfile) {
                state.unsubscribeUserProfile()
            }
        }
    }, [state.unsubscribeUserProfile])

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        // State
        ...state,
        // Actions
        initializeAuth,
        adminLogin,
        verifyOTP,
        selectOrganization,
        loadUserDealerships,
        switchDealership,
        signOut,
        resendOTP,
        refreshUserProfile,
        updateProfile
    }), [
        state,
        initializeAuth,
        adminLogin,
        verifyOTP,
        selectOrganization,
        loadUserDealerships,
        switchDealership,
        signOut,
        resendOTP,
        refreshUserProfile,
        updateProfile
    ])

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export default AuthContext
